import { QuestionResponseFormat } from '@mygpt/app/chat/constant';
import { Http } from '@mygpt/app/http';
import { PageInfo, ratingType, RecommendList, ReferenceSign } from '@mygpt/types';

export * from './session';

export interface questionsParams {
  ai_id: string;
  session_id: string;
  message_id: string;
  question: string;
  stream: boolean;
  debug_with_messages?: boolean; // 是否启用显示prompt，注意：需要设置stream为false
  max_tokens?: number; // 设置回答最大字数限制
  simple_question?: boolean; // 简易模式（速度快），复制模式（速度慢但更详细）
  with_source?: boolean; // 是否显示来源
  needToken?: boolean; // 是否带token，历史记录需要
  format?: QuestionResponseFormat; // 返回格式
  page_info?: { title: string; url: string }; // 页面信息
  created_by?: string; // 用来标识是哪个用户创建的，未登录情况下有效，如果不传则使用ip判断
  // question接口需要的动态背景信息，用来给Agent类型的Bot背后的Agent LLM理解用。
  // 这个参数的文档：https://vo216btkxyi.jp.larksuite.com/wiki/Tuj0wbxamiZbDwkVHDujCeWqpwd
  dynamic_background?: Array<{
    description: string; // 必填字段，用于描述该组动态背景信息的作用
    [key: string]: unknown; // 其他任意数量字段，任意值
  }>;
  language?: string; // 语言，en（英文），zh（中文），ja（日文），ko（韩文）
  agentMode?: string; // 模式，kiosk（大屏模式）
  user_identifier?: string; // 用户昵称
  tool_response?: boolean; // 对于 agent 类型的 bot，是否返回 tool call 和 tool response 详情
}

export type feedbackType = 'good' | 'bad';

export interface feedbackMetadata {
  type: feedbackType;
  value: string[];
}

export interface messageRatingParams {
  ai_id: string;
  session_id: string;
  message_id: string;
  rating: ratingType;
  source?: 'widget' | 'api' | 'playground' | 'share';

  feedback_content?: string;
  feedback_metadata: feedbackMetadata;
}

export interface metricsViewParams extends PageInfo {
  ai_id: string;
}

/**
 * 机器人问答
 * @param params
 * @returns
 */
export const questions = (params: questionsParams, signal: AbortSignal) => {
  const { fetch } = Http.use();
  return fetch(`/questions/${params.ai_id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      notNeedToken: params.needToken ? 'false' : 'true',
    },
    body: JSON.stringify(params),
    signal: signal,
  });
};

/**
 * 回答点赞
 * @param params
 * @returns
 */
export const messageRating = (params: messageRatingParams) => {
  const { patch } = Http.use();
  const { ai_id, message_id, rating, session_id, source, ...rest } = params;
  const sourceQuery = source ? `&source=${source}` : '';
  return patch(
    `/v1/${ai_id}/message_rating?session_id=${session_id}&message_id=${message_id}&rating=${rating}${sourceQuery}`,
    rest
  );
};

///questions/references/{ai_id}?message_id=
export const referencesSSE = (
  ai_id: string,
  message_id: string,
  callback: (done: boolean, params?: { type: string; data: number }) => void
) => {
  const { fetch } = Http.use();
  return fetch(`/questions/references/${ai_id}?message_id=${message_id}`).then((response) => {
    const handleResponse = async () => {
      const reader = response.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let done = false;
      while (!done) {
        const { done: doneReading, value } = await reader.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        try {
          chunkValue.split('\n').forEach((item) => {
            if (!item) return;
            callback(false, JSON.parse(item));
          });
          if (done) {
            callback(true);
          }
        } catch (error) {
          console.error(`Failed to parse chunkValue(${chunkValue}) error ${error}`, error);
        }
      }
    };
    handleResponse();
    return response;
  });
};

export const getReferencesService = (ai_id: string, message_id: string) => {
  const { get } = Http.use();
  return get<ReferenceSign[]>(`/questions/references/${ai_id}?message_id=${message_id}`);
};

/**
 * faq 相关问题 文字翻译
 * @param params
 * @returns
 */
export interface faqRecommendTranslationParams {
  ai_id: string;
  recommend_list: {
    id: string;
    name: string;
    language: string;
  }[];
}
export const faqRecommendTranslation = (params: faqRecommendTranslationParams) => {
  const { ai_id, ...rest } = params;
  const { post } = Http.use();
  return post<RecommendList[]>(`/v1/faq_recommend_translation/${ai_id}`, rest);
};

/**
 * 返回文件地址文件
 */
export interface getEmbeddingFileParams {
  file_id: string;
  to_pdf?: boolean;
}
export const getEmbeddingFile = (params: getEmbeddingFileParams) => {
  const { file_id, to_pdf } = params;
  const { defaults } = Http.use();
  return `${defaults.baseURL}/embeddings/files/${file_id}/download${to_pdf ? '?to_pdf=true' : ''}`;
};

// 带登录态下载知识库原文件：下载接口需要鉴权，不能再用 window.open 直开 URL（不带 Authorization 会 401）
export const downloadEmbeddingFile = async (params: getEmbeddingFileParams & { filename: string }) => {
  const { file_id, to_pdf, filename } = params;
  const http = Http.use();
  const response = await http.get(`/embeddings/files/${file_id}/download${to_pdf ? '?to_pdf=true' : ''}`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 100);
};

interface HighlightPositions {
  file_id: string;
  page: number;
  text: string;
}

export interface HighlightPositionsData {
  positions: Array<{
    page: number;
    matches: Array<Array<number>>;
  }>;
  success: boolean;
  message: string;
}

// 获取引用文本片段的对应于PDF中的矩形信息（用来渲染高亮矩形）
export const getHighlightPositions = (params: HighlightPositions) => {
  const { post } = Http.use();
  return post<HighlightPositionsData>(`/pdf/highlight`, {
    file_id: params.file_id,
    page: params.page,
    text: params.text,
  });
};

interface TableRAGCitationContentParams {
  file_id: string;
  sheet: string;
  rows: number[];
}

interface TableRAGCitationContentResponse {
  data: any[][];
  filename: string;
  sheet_name: string;
  success: boolean;
  message: string | null;
}

// 获取 TableRAG 引用中，对应的部分 excel 信息
export const getTableCitationContent = (params: TableRAGCitationContentParams) => {
  const { get } = Http.use();
  return get<TableRAGCitationContentResponse>(
    `table-citation?file_id=${params.file_id}&sheet=${params.sheet}&rows=${params.rows.join('&rows=')}`
  );
};

export const signatureClickAction = (params: { session_id: string; ai_id: string; icon: string; content: string }) => {
  const { post } = Http.use();
  return post(`/session/metrics/${params.ai_id}/click/signature`, {
    session_id: params.session_id,
    icon: params.icon,
    content: params.content,
  });
};

export interface SearchQuestionServiceParams {
  aiId: string;
  search: string;
  language: string;
}

export interface SearchQuestionItem {
  id: string;
  question: string;
  score?: number;
}

export const searchQuestionService = (params: SearchQuestionServiceParams) => {
  const { aiId, search, language } = params;

  const { get } = Http.use();
  return get<SearchQuestionItem[]>(`/questions/${aiId}/search?query=${search}&language=${language}`);
};

export const transferToHumanAction = (params: { ai_id: string; message_id: string }) => {
  const { ai_id, message_id } = params;
  const { post } = Http.use();
  return post(`/questions/${ai_id}/feedback/${message_id}`);
};

// 聊天上下文分享
export const saveChatContext = (params: { ai_id: string; chat_history: string }) => {
  const { post } = Http.use();
  return post<{ context_token: string }>('/chat_context', params);
};

export const getChatContext = (token: string) => {
  const { get } = Http.use();
  return get<{ chat_history: string; ai_id: string }>(`/chat_context/${token}`);
};

export const getReferenceText = (file_id: string, text: string) => {
  const { post } = Http.use();
  return post(`/embeddings/files/${file_id}/search`, { text: text });
};

export interface LLMMetricsWarning {
  health: false;
  // LLM 服务不正常的错误信息（英文的）
  warning: string;
}

export interface GPUMetrics {
  index: number;
  // GPU 利用率（0-100）
  gpu_util: number;
  // 显存总量（字节）
  memory_total: number;
  // 已用显存（字节）
  memory_used: number;
  // 显存利用率（0-100）
  mem_util: number;
  // 温度（摄氏度）
  temp_c: number;
}

export interface LLMMetricsData {
  // LLM 服务正常、健康
  health: true;
  // Number of requests currently running on GPU.
  num_requests_running: number;
  // Number of requests waiting to be processed.
  num_requests_waiting: number;
  // 已用 KV 缓存块的比例（0-1）
  kv_cache_usage_perc: number;
  // 各 GPU 指标
  GPU?: GPUMetrics[];
}

export type LLMMetricsResponse = LLMMetricsWarning | LLMMetricsData;

/**
 * 获取 LLM 运行指标
 * 包括健康状态、请求数量、延迟分位数等指标
 * @returns LLMMetricsResponse
 */
export const getLLMMetrics = () => {
  const { get } = Http.use();
  return get<LLMMetricsResponse>('/debug/llm_metrics');
};
